/* ==========================================================================
   ZONEX PHARMA — interaction layer
   Everything is namespaced and defensive: if a section is absent, nothing
   throws. Runs on DOMContentLoaded and again on Shopify section reload so it
   keeps working inside the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function once(el, key) {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  }

  /* ----------------------------------------------------- 1. mobile drawer */

  function initDrawer() {
    var drawer = $('[data-zx-drawer]');
    if (!drawer || !once(drawer, 'zxDrawer')) return;

    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var close = $('[data-zx-drawer-close]', drawer);
      if (close) close.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    $$('[data-zx-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });

    $$('[data-zx-drawer-close], .zx-drawer__scrim', drawer).forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Expand / collapse submenus inside the drawer
    $$('[data-zx-drawer-toggle]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = btn.parentElement && btn.parentElement.nextElementSibling;
        if (!panel) return;
        var isOpen = panel.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------- 2. search suggestions */

  function initSearch() {
    $$('[data-zx-search]').forEach(function (wrap) {
      if (!once(wrap, 'zxSearch')) return;
      var input = $('.zx-search__input', wrap);
      var panel = $('.zx-search__panel', wrap);
      if (!input || !panel) return;

      input.addEventListener('focus', function () {
        wrap.classList.add('is-open');
      });

      document.addEventListener('click', function (e) {
        if (!wrap.contains(e.target)) wrap.classList.remove('is-open');
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          wrap.classList.remove('is-open');
          input.blur();
        }
      });
    });
  }

  /* --------------------------------------------------- 3. seamless marquee */

  /* The Liquid already prints two identical groups. If the combined width is
     narrower than the viewport the loop would look sparse, so we clone until
     one group is at least as wide as the bar, then keep the copy in sync. */
  function initMarquee() {
    $$('[data-zx-marquee]').forEach(function (marquee) {
      if (!once(marquee, 'zxMarquee')) return;
      var track = $('.zx-marquee__track', marquee);
      if (!track) return;
      var groups = $$('.zx-marquee__group', track);
      if (groups.length < 2) return;

      var first = groups[0];
      var guard = 0;
      while (first.scrollWidth < marquee.offsetWidth && guard < 12) {
        var items = $$('.zx-marquee__item', first);
        if (!items.length) break;
        items.forEach(function (item) {
          first.appendChild(item.cloneNode(true));
        });
        guard++;
      }

      // Mirror group one into group two so the -50% wrap is pixel perfect
      groups[1].innerHTML = first.innerHTML;

      // Scale the duration with the content so speed stays constant
      var speed = parseFloat(marquee.dataset.zxSpeed || '60'); // px per second
      var duration = first.scrollWidth / (speed || 60);
      if (duration > 0 && isFinite(duration)) {
        track.style.setProperty('--zx-marquee-duration', duration.toFixed(2) + 's');
      }
    });
  }

  /* --------------------------------------------------------------- 4. tabs */

  function initTabs() {
    $$('[data-zx-tabs]').forEach(function (root) {
      if (!once(root, 'zxTabs')) return;
      var tabs = $$('[role="tab"]', root);
      if (!tabs.length) return;

      function select(tab) {
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.setAttribute('tabindex', selected ? '0' : '-1');
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(tab);
        });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (next) {
            e.preventDefault();
            select(next);
            next.focus();
          }
        });
      });
    });
  }

  /* -------------------------------------------------- 5. category view more */

  function initCategoryToggle() {
    $$('[data-zx-cats-toggle]').forEach(function (btn) {
      if (!once(btn, 'zxCats')) return;
      var grid = document.getElementById(btn.getAttribute('aria-controls'));
      if (!grid) return;

      btn.addEventListener('click', function () {
        var expanded = grid.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded
          ? btn.dataset.zxLabelLess || 'View less'
          : btn.dataset.zxLabelMore || 'View more';
      });
    });
  }

  /* --------------------------------------------------------------- 6. rails */

  function initRails() {
    $$('[data-zx-rail]').forEach(function (rail) {
      if (!once(rail, 'zxRail')) return;
      var track = rail.querySelector('.zx-rail__track');
      if (!track) return;

      var prev = rail.querySelector('[data-zx-rail-prev]');
      var next = rail.querySelector('[data-zx-rail-next]');

      function step() {
        var card = track.firstElementChild;
        if (!card) return track.clientWidth * 0.8;
        var styles = window.getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function sync() {
        // Arrows only earn their place when there is something off-screen.
        var overflowing = track.scrollWidth - track.clientWidth > 4;
        rail.classList.toggle('has-overflow', overflowing);
        if (!overflowing) return;
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      }

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
      // Images change the track width as they load
      setTimeout(sync, 400);
      setTimeout(sync, 1500);
    });
  }

  /* ------------------------------------------------------------ 7. quick add */

  /* The cart drawer is rendered from a snippet, not a section, so there is no
     #shopify-section-cart-drawer element to swap — which is why adds were
     silently not showing up. Hand the job to the theme's own CartDrawer /
     CartNotification component instead: ask /cart/add.js for the exact sections
     it wants, then let renderContents() paint them and open the drawer. This is
     the same path the product page form uses. */

  function cartComponent() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /* Mirrors assets/product-form.js as closely as possible so quick-add behaves
     exactly like the real product form. Two details matter and both were
     missing before:
       1. the drawer is a snippet, not a section, so we must let the theme's own
          CartDrawer.renderContents() paint it;
       2. <cart-drawer> keeps its `is-empty` class when the cart started empty,
          and `cart-drawer.is-empty .drawer__header { display: none }` blanks the
          panel even though the items are really there. product-form.js strips
          that class afterwards, so we do too. */
  function addToCart(variantId, quantity) {
    var cart = cartComponent();

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity || 1);

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    var addPath = (window.routes && window.routes.cart_add_url) || '/cart/add';

    return fetch(addPath, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (res) {
        if (res && res.status) {
          throw new Error(res.description || res.message || 'Could not add to cart');
        }
        if (!cart) {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return res;
        }
        if (typeof cart.renderContents === 'function') {
          cart.renderContents(res);
        }
        return res;
      })
      .finally(function () {
        // Must run whether or not rendering threw, exactly as the theme does
        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
        var inner = cart && cart.querySelector ? cart.querySelector('.drawer__inner') : null;
        if (inner) inner.classList.remove('is-empty');
        var items = document.querySelector('cart-drawer-items');
        if (items) items.classList.remove('is-empty');
      });
  }

  /* Delegated, so cards injected later (recommendations, recently viewed,
     section reloads in the editor) work without re-binding. */
  function initQuickAdd() {
    if (!once(document.body, 'zxQuickAddDelegated')) return;

    document.addEventListener('submit', function (e) {
      var form = e.target.closest ? e.target.closest('[data-zx-quick-add]') : null;
      if (!form) return;
      e.preventDefault();

      var btn = form.querySelector('[type="submit"]');
      var original = btn ? btn.textContent : '';
      var variantId = Number(form.dataset.zxVariant);
      if (!variantId) return;

      if (btn) {
        btn.classList.add('is-loading');
        btn.disabled = true;
      }

      function reset() {
        if (!btn) return;
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }

      addToCart(variantId, 1)
        .then(function () {
          reset();
          if (btn) {
            btn.textContent = 'Added';
            setTimeout(function () {
              btn.textContent = original;
            }, 1800);
          }
        })
        .catch(function (err) {
          reset();
          if (btn) {
            btn.textContent = (err && err.message) || 'Unavailable';
            setTimeout(function () {
              btn.textContent = original;
            }, 2400);
          }
        });
    });
  }

  /* ------------------------------------------------------ 8. recently viewed */

  var RV_KEY = 'zx_recently_viewed';
  var RV_MAX = 12;

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RV_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (err) {
      return [];
    }
  }

  function recordRecent() {
    var meta = $('[data-zx-record-product]');
    if (!meta) return;
    var handle = meta.dataset.zxRecordProduct;
    if (!handle) return;
    try {
      var list = readRecent().filter(function (h) {
        return h !== handle;
      });
      list.unshift(handle);
      window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
    } catch (err) {
      /* private browsing — silently skip */
    }
  }

  function initRecentlyViewed() {
    $$('[data-zx-recently-viewed]').forEach(function (section) {
      if (!once(section, 'zxRecent')) return;
      var mount = $('[data-zx-recent-mount]', section);
      if (!mount) return;

      var current = section.dataset.zxCurrentProduct || '';
      var limit = parseInt(section.dataset.zxLimit || '5', 10);
      var handles = readRecent()
        .filter(function (h) {
          return h && h !== current;
        })
        .slice(0, limit);

      if (!handles.length) {
        section.hidden = true;
        return;
      }

      Promise.all(
        handles.map(function (handle) {
          return fetch('/products/' + handle + '?section_id=zx-product-card-fragment')
            .then(function (r) {
              return r.ok ? r.text() : '';
            })
            .catch(function () {
              return '';
            });
        })
      ).then(function (chunks) {
        var html = chunks
          .map(function (chunk) {
            if (!chunk) return '';
            var doc = new DOMParser().parseFromString(chunk, 'text/html');
            var card = doc.querySelector('[data-zx-card]');
            return card ? card.outerHTML : '';
          })
          .filter(Boolean)
          .join('');

        if (!html) {
          section.hidden = true;
          return;
        }
        mount.innerHTML = html;
        section.hidden = false;
        initQuickAdd();
      });
    });
  }

  /* -------------------------------------------------- 9. cart label proxy */

  /* The cart label lives outside #cart-icon-bubble so the theme's own section
     re-render cannot wipe it. Forward its clicks to the real icon so the
     drawer still opens when someone taps the words. */
  function initCartProxy() {
    $$('[data-zx-cart-proxy]').forEach(function (proxy) {
      if (!once(proxy, 'zxCartProxy')) return;
      proxy.addEventListener('click', function (e) {
        var icon = document.getElementById('cart-icon-bubble');
        if (icon && icon.getAttribute('role') === 'button') {
          e.preventDefault();
          icon.click();
        }
      });
    });
  }

  /* ------------------------------------------- 10. sticky search toggle */

  function initSearchToggle() {
    $$('[data-zx-search-toggle]').forEach(function (btn) {
      if (!once(btn, 'zxSearchToggle')) return;
      var row = document.getElementById(btn.getAttribute('aria-controls'));
      if (!row) return;

      btn.addEventListener('click', function () {
        var open = row.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          var input = row.querySelector('.zx-search__input');
          if (input) input.focus();
        }
      });
    });
  }

  /* ------------------------------------------------- 11. certificate belt */

  function initCertBelt() {
    $$('[data-zx-certbelt]').forEach(function (belt) {
      if (!once(belt, 'zxCertBelt')) return;
      var track = belt.querySelector('.zx-certbelt__track');
      if (!track) return;
      var groups = $$('.zx-certbelt__group', track);
      if (groups.length < 2) return;

      var first = groups[0];
      var guard = 0;
      // Widen one group past the container so the belt never looks sparse
      while (first.scrollWidth < belt.offsetWidth && guard < 12) {
        var items = $$('.zx-certbelt__item', first);
        if (!items.length) break;
        items.forEach(function (item) {
          first.appendChild(item.cloneNode(true));
        });
        guard++;
      }
      groups[1].innerHTML = first.innerHTML;

      var speed = parseFloat(belt.dataset.zxSpeed || '40');
      var duration = first.scrollWidth / (speed || 40);
      if (duration > 0 && isFinite(duration)) {
        track.style.setProperty('--zx-belt-duration', duration.toFixed(2) + 's');
      }
    });
  }

  /* ------------------------------------------------------- 12. countdowns */

  function parseDeadline(value) {
    if (!value) return null;
    var m = String(value)
      .trim()
      .match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
    if (!m) {
      var fallback = new Date(value);
      return isNaN(fallback.getTime()) ? null : fallback;
    }
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] || 0),
      Number(m[5] || 0),
      0
    );
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function initCountdowns() {
    var nodes = $$('[data-zx-countdown]').filter(function (el) {
      return once(el, 'zxCountdown');
    });
    if (!nodes.length) return;

    var timers = nodes
      .map(function (el) {
        var deadline = parseDeadline(el.dataset.zxDeadline);
        if (!deadline) {
          el.hidden = true;
          return null;
        }
        return {
          el: el,
          deadline: deadline,
          days: el.querySelector('[data-zx-days]'),
          hours: el.querySelector('[data-zx-hours]'),
          minutes: el.querySelector('[data-zx-minutes]'),
          seconds: el.querySelector('[data-zx-seconds]'),
        };
      })
      .filter(Boolean);

    if (!timers.length) return;

    // Keep one shared registry: re-running boot() in the theme editor must add
    // new timers, not orphan the ones already on the page.
    window.__zxTimers = (window.__zxTimers || []).concat(timers);

    function tick() {
      var now = Date.now();
      window.__zxTimers.forEach(function (t) {
        var left = Math.floor((t.deadline.getTime() - now) / 1000);
        if (left <= 0) {
          t.el.classList.add('is-ended');
          if (t.days) t.days.textContent = '00';
          if (t.hours) t.hours.textContent = '00';
          if (t.minutes) t.minutes.textContent = '00';
          if (t.seconds) t.seconds.textContent = '00';
          var ended = t.el.dataset.zxEndedText;
          if (ended) t.el.setAttribute('aria-label', ended);
          return;
        }
        if (t.days) t.days.textContent = pad(Math.floor(left / 86400));
        if (t.hours) t.hours.textContent = pad(Math.floor((left % 86400) / 3600));
        if (t.minutes) t.minutes.textContent = pad(Math.floor((left % 3600) / 60));
        if (t.seconds) t.seconds.textContent = pad(left % 60);
      });
    }

    tick();
    window.clearInterval(window.__zxCountdownTimer);
    window.__zxCountdownTimer = window.setInterval(tick, 1000);
  }

  /* ------------------------------------------------------ 13. testimonials */

  function initReviews() {
    $$('[data-zx-reviews]').forEach(function (root) {
      if (!once(root, 'zxReviews')) return;

      var features = $$('[data-zx-rev-feature]', root);
      var items = $$('[data-zx-rev-item]', root);
      var bar = root.querySelector('[data-zx-rev-progress]');
      if (features.length < 1 || items.length < 1) return;

      var total = items.length;
      var index = 0;
      var seconds = parseFloat(root.dataset.zxInterval || '10');
      if (!isFinite(seconds) || seconds < 2) seconds = 10;
      var timer = null;

      var reduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function show(next) {
        index = ((next % total) + total) % total;

        features.forEach(function (f, i) {
          f.hidden = i !== index;
        });

        // Rotate the list so the active review sits at the top, the way the
        // reference site behaves, without touching the DOM order.
        items.forEach(function (item, i) {
          item.classList.toggle('is-active', i === index);
          item.style.order = String((i - index + total) % total);
        });

        if (bar && !reduced) {
          bar.style.transition = 'none';
          bar.style.width = '0%';
          // Force a reflow so the restarted transition actually runs
          void bar.offsetWidth;
          bar.style.transition = 'width ' + seconds + 's linear';
          bar.style.width = '100%';
        }
      }

      function start() {
        stop();
        if (total < 2 || reduced) return;
        timer = window.setInterval(function () {
          show(index + 1);
        }, seconds * 1000);
      }

      function stop() {
        if (timer) window.clearInterval(timer);
        timer = null;
      }

      items.forEach(function (item, i) {
        item.addEventListener('click', function () {
          show(i);
          start();
        });
        item.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            show(i);
            start();
          }
        });
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);

      show(0);
      start();
    });
  }

  /* ------------------------------------------------------- 14. back to top */

  function initBackToTop() {
    var btn = document.querySelector('[data-zx-top]');
    if (!btn || !once(btn, 'zxTop')) return;

    function sync() {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      btn.classList.toggle('is-visible', y > 400);
    }

    btn.addEventListener('click', function () {
      var reduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });

    window.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ------------------------------------------------------------------ boot */


  /* ------------------------------------------------------------------ */
  /* Scroll dots under a rail — small screens only                       */
  /*                                                                     */
  /* On a phone a rail shows one or two cards with nothing to say the    */
  /* row continues, and the arrows are hidden at that width. Dots give   */
  /* the visitor that signal and a way to move.                          */
  /*                                                                     */
  /* Built entirely here, so no section markup had to change. A rail     */
  /* inside a hidden tab has no width until it is shown, which is why    */
  /* ResizeObserver rebuilds the dots when a panel becomes visible.      */
  /* ------------------------------------------------------------------ */
  var DOTS_MAX_WIDTH = 989;

  function initRailDots() {
    $$('.zx-rail').forEach(function (rail) {
      var track = rail.querySelector('.zx-rail__track');
      if (!track || track.dataset.zxDotsReady === 'true') return;
      track.dataset.zxDotsReady = 'true';

      var dots = document.createElement('div');
      dots.className = 'zx-rail__dots';
      dots.setAttribute('role', 'tablist');
      dots.setAttribute('aria-label', 'Scroll position');
      track.parentNode.insertBefore(dots, track.nextSibling);

      function pageCount() {
        if (!track.clientWidth) return 0;
        return Math.ceil(track.scrollWidth / track.clientWidth);
      }

      function currentPage() {
        if (!track.clientWidth) return 0;
        return Math.round(track.scrollLeft / track.clientWidth);
      }

      function paint() {
        var active = currentPage();
        Array.prototype.forEach.call(dots.children, function (dot, i) {
          dot.setAttribute('aria-current', i === active ? 'true' : 'false');
        });
      }

      function build() {
        var wide = window.innerWidth > DOTS_MAX_WIDTH;
        var pages = pageCount();

        // Nothing to page through, or we are on a desktop
        if (wide || pages < 2) {
          dots.classList.remove('is-on');
          dots.innerHTML = '';
          return;
        }

        if (dots.children.length !== pages) {
          dots.innerHTML = '';
          for (var i = 0; i < pages; i++) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'zx-rail__dot';
            dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
            dot.dataset.zxDot = i;
            dot.addEventListener('click', function () {
              track.scrollTo({
                left: track.clientWidth * parseInt(this.dataset.zxDot, 10),
                behavior: 'smooth'
              });
            });
            dots.appendChild(dot);
          }
        }

        dots.classList.add('is-on');
        paint();
      }

      track.addEventListener('scroll', paint, { passive: true });
      window.addEventListener('resize', build);

      // Fires when a hidden tab panel is revealed and the track gains width
      if ('ResizeObserver' in window) {
        new ResizeObserver(build).observe(track);
      }

      build();
    });
  }

  function boot() {
    initRailDots();
    initDrawer();
    initCartProxy();
    initSearchToggle();
    initSearch();
    initMarquee();
    initTabs();
    initCategoryToggle();
    initRails();
    initCertBelt();
    initCountdowns();
    initReviews();
    initQuickAdd();
    initBackToTop();
    recordRecent();
    initRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-run after a section is re-rendered
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);
  window.addEventListener('resize', function () {
    // Marquee duration depends on width; recompute on orientation change
    $$('[data-zx-marquee]').forEach(function (m) {
      m.dataset.zxMarquee = '';
    });
    initMarquee();
  });
})();
